import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Symbol } from "../GameComponents/Symbol";
import { PaylinesDefinitions } from "../GameComponents/PaylinesDefinitions";
import { ReflectionElement } from "./ReflectionElement";
import { Payline } from "../GameComponents/Payline";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { MachineExtended } from "./MachineExtended";
import * as Communication from "neo-games/Engine/Communication/index";
import { AppContext } from "../../Common/AppContext";
import { BetManager } from "../Managers/BetManager";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { NumberUtils } from "neo-common/Engine/Utils/NumberUtils";
import { Languages } from "../Utils/Languages";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { PlayList } from "../Components/Sounds/PlayList";

/**
 * WinAnimator uses paylines and symbols to create presentation of wins to user.
 * Also handle high wins animation for presentation special screens to user when win is bigger than some defined values.
 * @class WinAnimator
 * @extends ContainerElement
 */

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorExtended extends WinAnimator {
    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    public static ShowBackgroundSymbols: string = "eventShowBackgroundSymbols";

    //******************************************************************************************************************
    //  SETTINGS
    //******************************************************************************************************************

    private static WAVE_SINGLE_DELAY: number = 6;

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    protected _initWinsFinisedTimer: FrameTimerEventEmitter;

    private _baseReelBackground: SwitchElement;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this._initWinsFinisedTimer = CreateFrameTimer("InitWinsFinishedTimer");
        this._initWinsFinisedTimer.on(FrameTimerEvents.Complete, this.finishFirstInitWins.bind(this));
    }

    //******************************************************************************************************************
    //  INIT
    //******************************************************************************************************************

    public setupReflectionElement(reflectionElement: ReflectionElement, baseReelBackground: SwitchElement): void {
        if (this.value != null) {
            reflectionElement.addElement(this.value);
        }

        if (baseReelBackground) {
            this._baseReelBackground = baseReelBackground;
        }
    }

    //******************************************************************************************************************
    //  ON REEL STOPPED, SHOW SYMBOLS
    //******************************************************************************************************************

    public onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        super.onReelStopped(reelsData, reelIndex);

        if (reelsData[reelIndex].symbols.indexOf(12) !== -1) {
            PlayList.play("SND_BaseWildHit" + reelIndex);
        }
    }

    //******************************************************************************************************************
    //  SHOW WINS
    //******************************************************************************************************************

    public showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
        let result = super.showWins(wins, win);

        this.prepareBackgroundSymbols();

        return result;
    }

    public showInitWins(wins: Array<Communication.Game.IWin>, win: number): void {
        this.currentWins = wins;
        this.currentWin = win;

        this.resetAnimations();

        if (wins != null && wins.length > 0) {
            this.showCustomInitWin();
            this.showFirstInitWins();

            this._initWinsFinisedTimer.start(120);
        } else {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
        }

        this.prepareBackgroundSymbols(true);
    }

    private prepareBackgroundSymbols(init: boolean = false): void {
        let winSymbolIds: Array<number> = [];
        let hasWild: boolean = false;

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                const symbol = this.currentSymbols[i][j];
                if (symbol.getCurrentCase() == "WIN" && winSymbolIds.indexOf(symbol.id + 1) == -1) {
                    if (symbol.id != 11) {
                        winSymbolIds.push(symbol.id + 1);
                    } else {
                        hasWild = true;
                    }
                }
            }
        }

        this.emit(WinAnimatorExtended.ShowBackgroundSymbols, winSymbolIds, hasWild, init);
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    protected finishFirstWins(): void {
        this.emit(WinAnimator.FinishWinAnimator);

        if (this.currentWins && this.currentWins.length > 0) {
            for (let i = 1; i < this.currentWins.length; i++) {
                this.winSymbolsGoToCase(i, Symbol.CaseWin + "_RESET", false);
            }

            this.currentCycle = 0;

            this.cycleWins();
        }
    }

    protected finishFirstInitWins(): void {
        this.currentCycle = 0;

        if (this.currentWins && this.currentWins.length > 0) {
            for (let i = 1; i < this.currentWins.length; i++) {
                this.winSymbolsGoToCase(i, Symbol.CaseWin + "_RESET", false);
            }

            this.cycleWins();
        }
    }

    protected winSymbolsGoToCaseAsWay(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                if ((win.mask[i] & (1 << j)) !== 0 && this.allowSymbolGoToCase(i, j, caseType)) {
                    const symbol = this.currentSymbols[i][j];
                    symbol.setDynamicTime(WinAnimatorExtended.WAVE_SINGLE_DELAY * i);
                    symbol.goToCase(caseType);

                    this.symbolsContainer.addChild(symbol);

                    symbols.push(symbol);
                }
            }
        }

        return symbols;
    }

    //******************************************************************************************************************
    //  ANIMATIONS
    //******************************************************************************************************************

    protected resetAnimations(): void {
        super.resetAnimations();

        if (this._baseReelBackground) {
            this._baseReelBackground.goToCase("BASE");
        }
    }

    protected startAnimation(): void {
        super.startAnimation();

        if (this._baseReelBackground) {
            let caseType = "BASE";
            switch (this.currentAnimationIndex) {
                case 3:
                    caseType = "HW_BIG";
                    break;
                case 4:
                    caseType = "HW_EPIC";
                    break;
                case 5:
                    caseType = "HW_LEGENDARY";
                    break;
            }

            this._baseReelBackground.goToCase(caseType);
        }
    }

    protected stopAnimation(): void {
        if (this.currentAnimationIndex > 2 && this._baseReelBackground) {
            this._baseReelBackground.goToCase("HW_OUTRO");
        }

        super.stopAnimation();
    }

    //******************************************************************************************************************
    //  TIMER
    //******************************************************************************************************************

    protected stopTimer(): void {
        super.stopTimer();

        if (this._initWinsFinisedTimer != null) {
            this._initWinsFinisedTimer.stop();
        }
    }

    //******************************************************************************************************************
    //  IDLE
    //******************************************************************************************************************

    protected idle(): void {
        //NO IDLE
    }

    public getColorElements(): Array<SwitchElement> {
        let colorElements: Array<SwitchElement> = [];

        if (this.value) {
            colorElements.push(this.value.getElements().childs["Flare"]);
        }

        for (let i = 0; i < 3; i++) {
            colorElements.push(this.animations[i].getElements().childs["Flare"]);
        }

        return colorElements;
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorExtended.ts
 **/